// Hero reveal on load — skipped when a blink overlay is present (homepage
// only); blink.js owns the hero reveal there so it lands as the eye
// dissolves instead of finishing silently underneath an opaque overlay.
window.addEventListener('load', () => {
  const hero = document.getElementById('hero');
  const hasBlink = document.getElementById('blink-overlay');
  if (hero && !hasBlink) requestAnimationFrame(() => hero.classList.add('revealed'));
});

// Grain-to-clarity scroll reveal. Toggling `.in` (the CSS opacity/blur
// transition trigger) the instant an element crosses the intersection
// threshold assumes its content is already there to reveal — true for
// text, not guaranteed for an <img> that's still mid-fetch (lazy-loaded,
// or just a slow connection). Without waiting, the transition starts
// against a blank box and the real pixels "pop" in separately whenever
// the fetch actually finishes — reading as the image itself being slow
// to fade in, reported live 2026-08-19. Same wait-for-real-readiness
// idiom already used for the intro's eye image (`whenEyePainted`,
// blink.js) — applied here too instead of assuming the image is already
// painted.
function revealWhenImageReady(el) {
  const img = el.tagName === 'IMG' ? el : el.querySelector('img');
  // offsetParent is null for any element hidden by `display:none` on itself
  // or an ancestor (e.g. .service-row-img, mobile-only, hidden above 820px)
  // — a genuinely non-rendered image will never fire `load`/`error` (the
  // browser's lazy-load heuristic has no geometry to queue the fetch
  // against), which hung this reveal forever at desktop widths. Treat a
  // hidden image the same as "no image" instead of waiting on it.
  const imgIsRendered = img && img.offsetParent !== null;
  if (!img || !imgIsRendered || (img.complete && img.naturalWidth > 0)) {
    el.classList.add('in');
    return;
  }
  const done = () => el.classList.add('in');
  img.addEventListener('load', done, { once: true });
  img.addEventListener('error', done, { once: true }); // never hang on a failed fetch
  // Safety net: reported live (2026-08-21, Shopify) as images "not fully
  // unblurring" on real project/service pages — `load`/`error` are the
  // only two ways this ever resolves, and if a slow connection, a CDN
  // hiccup, or the browser simply not having started the lazy-load fetch
  // yet delays both indefinitely, the reveal was stuck at its dim/blurred
  // resting state forever, with nothing to fall back on. A few seconds is
  // long enough for any real image to have started responding; past that,
  // show the section anyway rather than leave it permanently half-there.
  setTimeout(done, 4000);
}

// Trigger point moved earlier (2026-08-20, per explicit request — was
// reading as "blank, then the image pops in around halfway up the
// screen"). rootMargin's bottom value now POSITIVE, not negative: it
// grows the observed area downward past the real viewport edge, so an
// element counts as "intersecting" while it's still up to 25% of a
// viewport-height below the fold, well before it's actually visible.
// threshold dropped to a bare sliver (not 0 outright, so a fully
// off-screen element with a 0-height edge case can't spuriously fire)
// so the reveal starts the moment it crosses into that earlier zone,
// rather than waiting for a meaningful chunk of it to show first.
const io = new IntersectionObserver((entries) => {
  entries.forEach((entry) => {
    if (entry.isIntersecting) {
      io.unobserve(entry.target);
      revealWhenImageReady(entry.target);
    }
  });
}, { threshold: .01, rootMargin: '0px 0px 25% 0px' });

// `.meliza-cover-timed-reveal` elements (masthead + portrait) opt out of
// this generic threshold-based reveal — they're revealed instead by
// motion.js's initMelizaCoverReveal, each on its own "top 85%" trigger, so
// they only animate once actually approaching the viewport rather than
// resolving early. Reduced-motion/no-GSAP still shows them via the
// sitewide `.reveal-el{opacity:1!important}` CSS rule, so they're never
// left permanently hidden if that replacement doesn't run.
document.querySelectorAll('.reveal-el').forEach((el) => {
  if (el.classList.contains('meliza-cover-timed-reveal')) return;
  io.observe(el);
});
